import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { UpsellSurface } from 'fym-shared/src/music24/watermarkUpsell';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { watermarkChoiceMessages } from '../choice/watermarkChoiceMessages';
import { useOpenWatermarkOffer } from '../useOpenWatermarkOffer';
import { useUpsellShown } from '../useUpsellShown';
import { useWatermarkSurface } from '../useWatermarkSurface';
import { watermarkFileMessages as messages } from './watermarkFileMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
import { PaidPlanLine } from './PaidPlanLine';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WatermarkCalloutProps {
  /** `file`: a watermarked file is open (a3). `make`: before a render that costs credits (a6). */
  variant: 'file' | 'make';
  surface: UpsellSurface;
  /** `file`: the file is a half-size preview render. */
  preview?: boolean;
  /** `make`: the credits of the render. */
  credits?: number;
  className?: string;
}

/**
 * The pink callout (design: trial-upsell a3, a6 `.tu-call`). It shows on Free and the trial only,
 * never for the demo artist. It says "trial" on the trial and "Free" on Free. A member reads the
 * owner note and gets no button.
 */
const WatermarkCalloutComponent: React.FC<WatermarkCalloutProps> = (props) => {
  const { variant, surface, preview = false, credits = 0, className } = props;
  const intl = useIntl();
  const { applies, plan, isMember, summary } = useWatermarkSurface();
  const openOffer = useOpenWatermarkOffer();
  const remove = useCallback(() => openOffer(surface), [openOffer, surface]);
  useUpsellShown(surface, applies);
  if (!applies) {
    return null;
  }
  const isTrial = plan === 'trial';
  const promisesCopies = summary.cleanCopiesAvailable && summary.files > 0;
  let title: string;
  let body: string;
  if (variant === 'make') {
    title = intl.formatMessage(isTrial ? messages.makeTitleTrial : messages.makeTitleFree);
    body = intl.formatMessage(messages.makeBody, { credits });
  } else {
    title = intl.formatMessage(messages.fileTitle);
    let gives = intl.formatMessage(messages.planGives);
    if (!summary.cleanCopiesAvailable) {
      // The licence had its one run (spec §14.1): a plan gives only new files with no watermark.
      gives = intl.formatMessage(messages.bannerBodyNoCopies);
    } else if (promisesCopies) {
      gives = intl.formatMessage(isTrial ? messages.planGivesTrial : messages.planGivesFree, { count: summary.files });
    }
    body = preview ? `${intl.formatMessage(messages.halfSize)} ${gives}` : gives;
  }

  return (
    <div className={clsx(styles.call, className)} data-testid={`watermark-callout-${variant}`}>
      <b>{withBrandWord(title)}</b>
      <small>{withBrandWord(body)}</small>
      {isMember ? (
        <small data-testid="watermark-callout-owner-note">
          {intl.formatMessage(watermarkChoiceMessages.ownerNote)}
        </small>
      ) : (
        <>
          <SMLButton variant="primary" onClick={remove}>
            {intl.formatMessage(watermarkChoiceMessages.remove)}
          </SMLButton>
          <PaidPlanLine />
        </>
      )}
      {variant === 'make' && summary.cleanCopiesAvailable && (
        <small data-testid="watermark-callout-fine">{intl.formatMessage(messages.makeFine)}</small>
      )}
    </div>
  );
};

export const WatermarkCallout = TypedMemo(WatermarkCalloutComponent);
